import { cEl } from '../../../../util';
import { deleteSubmissions, downloadSubmissions } from '../../../../api/submission';
import { navigateTo } from '../../../AppHeader/Link';
import { Litepicker } from 'litepicker';
import { CHECK_OPTION_ALL, getFullPath, initializePersistentState, persistentState } from './Table';
import { getSubmissionsQueryParam } from './index';
import { initializeConfirmModal, showConfirmModal } from './Modal';
import Icon from '../../../Common/Icon';

export const DEFAULT_SEARCH_START_OFFSET_DAYS = 30;

function SearchBox({ param, page, rows = [], sort = [] }) {
    const { property: col, direction } = sort[0] || {};
    const searchBox = cEl('div');
    searchBox.classList.add('search-box');

    const actionBoxWrapper = cEl('div');
    actionBoxWrapper.append(ActionBox({ param, rows, page, col, direction }));

    searchBox.append(actionBoxWrapper);

    const searchForm = cEl('form');
    searchForm.classList.add('search-form');

    const startDateContainer = cEl('div');
    startDateContainer.classList.add('datepicker-container');
    const startDate = cEl('input');
    startDate.classList.add('datepicker');
    startDateContainer.append(Icon({ iconName: 'calendar_month' }), startDate);

    const endDateContainer = cEl('div');
    endDateContainer.classList.add('datepicker-container');
    const endDate = cEl('input');
    endDate.classList.add('datepicker');
    endDateContainer.append(Icon({ iconName: 'calendar_month' }), endDate);

    const today = param.endDate ? Number(param.endDate) * 1000 : getEndOfDay(Date.now());

    const defaultStartDate = param.startDate
        ? Number(param.startDate) * 1000
        : getStartOfDay(subtractDay(Date.now(), DEFAULT_SEARCH_START_OFFSET_DAYS));

    const startDatePicker = new Litepicker({
        element: startDate,
        lang: 'ko',
        format: 'YYYY-MM-DD',
        startDate: defaultStartDate,
        maxDate: today,
        setup: picker => {
            picker.on('selected', date => {
                endDatePicker.options.minDate = date;
            });
        },
    });
    const endDatePicker = new Litepicker({
        element: endDate,
        lang: 'ko',
        format: 'YYYY-MM-DD',
        startDate: today,
        minDate: defaultStartDate,
        setup: picker => {
            picker.on('selected', date => {
                startDatePicker.options.maxDate = date;
            });
        },
    });

    const keywordContainer = cEl('div');
    keywordContainer.classList.add('keyword-container');
    const keyword = cEl('input');
    keyword.classList.add('search-keyword');
    keyword.type = 'text';
    keyword.placeholder = '이름, 소속 기관, 문의 내용을 검색하세요';
    keyword.maxLength = 100;
    keyword.value = param.keyword ?? '';
    const searchIcon = Icon({ iconName: 'search' });
    keywordContainer.append(searchIcon, keyword);

    const searchButton = cEl('button');
    searchButton.classList.add('button', 'brand-button', 'search-button');
    searchButton.innerHTML = '검색';
    searchButton.addEventListener('click', e => {
        e.preventDefault();
        initializePersistentState();
        const param = {
            col,
            direction,
            startDate: msToS(getStartOfDay(startDatePicker.getDate().timestamp())),
            endDate: msToS(getEndOfDay(endDatePicker.getDate().timestamp())),
            page: 1,
        };
        if (keyword.value.trim()) {
            param.keyword = keyword.value.trim();
        }
        navigateTo(getFullPath(param));
    });

    searchForm.append(startDateContainer, '~', endDateContainer, keywordContainer, searchButton);

    searchBox.append(searchForm);

    return searchBox;
}

export function ActionBox({ submissionNum }) {
    initializeConfirmModal();
    const actionContainer = cEl('div');
    actionContainer.classList.add('action-container');

    const downloadButton = cEl('button');
    downloadButton.classList.add('button', 'default-button');
    downloadButton.innerHTML = '내려받기';
    downloadButton.addEventListener('click', () => {
        const submissionNums = (
            submissionNum
                ? [submissionNum]
                : [...document.querySelectorAll('.submissions-container [name="submissionNum"]:checked')].map(
                      ({ value }) => value,
                  )
        ).join();
        if (persistentState[CHECK_OPTION_ALL]) {
            const param = getSubmissionsQueryParam();
            const excelParam = { startDate: param.startDate, endDate: param.endDate };
            if (param.keyword) {
                excelParam.keyword = param.keyword;
            }
            if (persistentState.excludedSubmissionNums.length) {
                excelParam.excludedSubmissionNums = persistentState.excludedSubmissionNums.join();
            }
            downloadSubmissions(excelParam).then(downloadSubmissionsExcel);
        } else if (submissionNums) {
            downloadSubmissions({ submissionNums }).then(downloadSubmissionsExcel);
        }
    });
    actionContainer.append(downloadButton);

    const deleteButton = cEl('button');
    deleteButton.classList.add('button', 'danger-button');
    deleteButton.innerHTML = '삭제';
    deleteButton.addEventListener('click', async () => {
        const confirmed = await showConfirmModal({
            title: '삭제 확인',
            message: '선택된 문의 내역을 삭제 하시겠습니까?',
            confirmText: '네',
            cancelText: '아니오',
        });
        if (confirmed) {
            const submissionNums = (
                submissionNum
                    ? [submissionNum]
                    : [...document.querySelectorAll('.submissions-container [name="submissionNum"]:checked')].map(
                          ({ value }) => value,
                      )
            ).join();
            if (persistentState[CHECK_OPTION_ALL]) {
                const param = getSubmissionsQueryParam();
                const deleteParam = { startDate: param.startDate, endDate: param.endDate };
                if (param.keyword) {
                    deleteParam.keyword = param.keyword;
                }
                if (persistentState.excludedSubmissionNums.length) {
                    deleteParam.excludedSubmissionNums = persistentState.excludedSubmissionNums.join();
                }
                deleteSubmissions(deleteParam).then(() => {
                    navigateTo(getFullPath(param));
                });
            } else if (submissionNums) {
                deleteSubmissions({ submissionNums }).then(() => {
                    if (submissionNum) {
                        window.history.back();
                    } else {
                        navigateTo(getFullPath(getSubmissionsQueryParam()));
                    }
                });
            }
        }
    });

    actionContainer.append(deleteButton);

    return actionContainer;
}

function getDownloadDate() {
    const now = new Date();

    const year = String(now.getFullYear()).slice(-2); // 마지막 2자리만
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    return `${year}${month}${day}`;
}

export const getUnixTimestamp = date => {
    return date ? msToS(new Date(date).getTime()) : msToS(Date.now());
};

export const msToS = timestampMs => {
    return Math.floor(timestampMs / 1000);
};

export const getEndOfDay = timestamp => {
    let normalized = timestamp;
    let isMs = true;
    if (timestamp && String(timestamp).length === 10) {
        normalized = timestamp * 1000;
        isMs = false;
    }
    const day = normalized ? new Date(normalized) : new Date();
    day.setHours(23, 59, 59, 999);
    return isMs ? day.getTime() : getUnixTimestamp(day);
};

export const getStartOfDay = timestamp => {
    let normalized = timestamp;
    let isMs = true;
    if (timestamp && String(timestamp).length === 10) {
        normalized = timestamp * 1000;
        isMs = false;
    }
    const day = normalized ? new Date(normalized) : new Date();
    day.setHours(0, 0, 0, 0);
    return isMs ? day.getTime() : getUnixTimestamp(day);
};

export const subtractDay = (timestamp, amount) => {
    let normalized = timestamp;
    let isMs = true;
    if (timestamp && String(timestamp).length === 10) {
        normalized = timestamp * 1000;
        isMs = false;
    }
    const day = normalized ? new Date(normalized) : new Date();
    day.setDate(day.getDate() - amount);
    return isMs ? day.getTime() : getUnixTimestamp(day);
};

export const downloadSubmissionsExcel = res => {
    const disposition = res.headers['content-disposition'];
    const blob = res.data;
    let filename = `온라인평가시스템_${getDownloadDate()}.zip`;

    // disposition 접근 될 경우(same origin || 서버설정 필요)
    const match = disposition && disposition.match(/filename\*?=(?:UTF-8'')?"?([^"]+)"?/);
    if (match && match[1]) {
        filename = decodeURIComponent(match[1]);
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

export default SearchBox;
